import React from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  daysRemainingLabel,
  formatDueDate,
  formatUpdatedAt,
  STATUS_COLOR,
} from "@/lib/status";
import StatusBadge, { StatusDot } from "@/components/StatusBadge";
import DocumentCell from "@/components/DocumentCell";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";

function SortHeader({ label, field, current, onSort, testId, align = "left" }) {
  const isActive = current.field === field;
  const Icon = !isActive
    ? ArrowUpDown
    : current.dir === "asc"
    ? ArrowUp
    : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={cn(
        "inline-flex items-center gap-1 hover:text-[#0F172A]",
        isActive && "text-[#0F172A]"
      )}
      data-testid={testId}
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
}

function DaysCell({ task }) {
  const label = daysRemainingLabel(task);
  const derived = task.derivedStatus;
  const c = STATUS_COLOR[derived] || STATUS_COLOR.Pending;
  return (
    <span
      className="text-xs font-medium tabular-nums"
      style={{ color: derived === "Overdue" ? c.fg : "#0F172A" }}
    >
      {label}
    </span>
  );
}

export default function ComplianceTable({
  rows,
  sort,
  onSort,
  onToggleComplete,
  onEdit,
  onDelete,
  onUpload,
  onRemoveDoc,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}) {
  const allSelected =
    rows.length > 0 && rows.every((r) => selectedIds?.has?.(r.id));
  const someSelected = rows.some((r) => selectedIds?.has?.(r.id));
  return (
    <div
      className="ab-card ab-shadow-sm overflow-hidden"
      data-testid={TID.complianceTable}
    >
      <div className="overflow-x-auto">
        <table className="ab-table w-full text-left">
          <thead>
            <tr>
              <th style={{ width: 36 }} className="text-center">
                <input
                  type="checkbox"
                  aria-label="Select all tasks"
                  data-testid={TID.taskSelectAll}
                  className="h-3.5 w-3.5 accent-[#0F172A] align-middle"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) =>
                    onToggleSelectAll?.(rows.map((r) => r.id), e.target.checked)
                  }
                />
              </th>
              <th style={{ width: 20 }}>&nbsp;</th>
              <th>
                <SortHeader
                  label="Task"
                  field="title"
                  current={sort}
                  onSort={onSort}
                  testId={TID.sortTitle}
                />
              </th>
              <th>Category</th>
              <th>
                <SortHeader
                  label="Due Date"
                  field="dueDate"
                  current={sort}
                  onSort={onSort}
                  testId={TID.sortDue}
                />
              </th>
              <th>Days Remaining</th>
              <th>
                <SortHeader
                  label="Status"
                  field="status"
                  current={sort}
                  onSort={onSort}
                  testId={TID.sortStatus}
                />
              </th>
              <th>Remarks</th>
              <th>Document</th>
              <th>
                <SortHeader
                  label="Updated"
                  field="updatedAt"
                  current={sort}
                  onSort={onSort}
                  testId={TID.sortUpdated}
                />
              </th>
              <th style={{ width: 132 }} className="text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((task) => (
              <tr key={task.id} data-testid={TID.taskRow(task.id)}>
                <td className="text-center">
                  <input
                    type="checkbox"
                    aria-label={`Select ${task.title}`}
                    data-testid={TID.taskSelect(task.id)}
                    className="h-3.5 w-3.5 accent-[#0F172A] align-middle"
                    checked={!!selectedIds?.has?.(task.id)}
                    onChange={(e) => onToggleSelect?.(task.id, e.target.checked)}
                  />
                </td>
                <td>
                  <StatusDot status={task.derivedStatus} />
                </td>
                <td
                  className="max-w-[320px]"
                  data-testid={TID.taskTitle(task.id)}
                >
                  <div className="font-medium text-[#0F172A] truncate">
                    {task.title}
                  </div>
                </td>
                <td>
                  <span className="text-xs text-[#334155] bg-[#F1F5F9] rounded px-1.5 py-0.5 font-medium">
                    {task.category}
                  </span>
                </td>
                <td className="text-[#0F172A] tabular-nums">
                  {formatDueDate(task.dueDate)}
                </td>
                <td>
                  <DaysCell task={task} />
                </td>
                <td>
                  <StatusBadge status={task.derivedStatus} />
                </td>
                <td
                  className="max-w-[220px]"
                  data-testid={TID.taskRemarks(task.id)}
                >
                  {task.remarks ? (
                    <div
                      className="text-xs text-[#334155] truncate"
                      title={task.remarks}
                    >
                      {task.remarks}
                    </div>
                  ) : (
                    <span className="text-xs text-[#94A3B8]">—</span>
                  )}
                </td>
                <td>
                  <DocumentCell
                    task={task}
                    onUpload={onUpload}
                    onRemove={onRemoveDoc}
                  />
                </td>
                <td className="text-xs text-[#64748B] tabular-nums whitespace-nowrap">
                  {formatUpdatedAt(task.updatedAt)}
                </td>
                <td className="text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onToggleComplete(task)}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md px-2 h-7 text-xs font-medium border",
                        task.status === "Completed"
                          ? "bg-white border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]"
                          : "bg-[#0F172A] border-[#0F172A] text-white hover:bg-[#1E293B]"
                      )}
                      data-testid={TID.taskToggle(task.id)}
                      title={
                        task.status === "Completed"
                          ? "Mark as pending"
                          : "Mark as completed"
                      }
                    >
                      <Check className="h-3 w-3" />
                      {task.status === "Completed" ? "Undo" : "Done"}
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(task)}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]"
                      data-testid={TID.taskEdit(task.id)}
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(task)}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#FEF2F2] hover:text-[#DC2626] hover:border-[#FECACA]"
                      data-testid={TID.taskDelete(task.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
